import React, { useEffect, useState } from 'react';
import { Clock, AlertCircle, X, SquarePen } from 'lucide-react';
import { getShiftEventsByID } from '../../../services/superAdminServices';
import EventModal from './NewShiftModal';
import SuccessModal from '../../../components/ConfirmModal/SuccessModal';

interface TimelineItemProps {
  time: string;
  description: string;
  onEditClick?: () => void;
  isSignedOff?: boolean;
}

interface ShiftEvent {
  id: number;
  shift_log_id: number;
  event_time: string;
  description: string;
  created_at: string;
}

export function TimelineItem({ time, description, onEditClick, isSignedOff }: TimelineItemProps) {
  return (
    <div className="relative pl-8 pb-8">
      <div className="absolute left-[7px] top-[6px] w-2.5 h-2.5 rounded-full bg-blue-500 z-10" />

      <div className="flex items-center gap-2 mb-2">
        <div className="flex items-center gap-2 text-gray-600 text-sm">
          <Clock className="w-4 h-4" /> {time}
        </div>

        {!isSignedOff && (
          <button type="button" className="text-gray-400 hover:text-red-600" onClick={onEditClick}>
            <SquarePen className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="text-gray-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: description }} />
    </div>
  );
}

interface InfoTimelineProps {
  shiftLogId: number;
  isSignedOff: boolean;
  readOnly?: boolean;
}

export default function InfoTimeline({ shiftLogId, isSignedOff, readOnly = false }: InfoTimelineProps) {
  const [events, setEvents] = useState<ShiftEvent[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ShiftEvent | null>(null);
  const [wasEditAction, setWasEditAction] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [apiError, setApiError] = useState('');
  const [showError, setShowError] = useState(false);

  const fetchEvents = async () => {
    try {
      const response = await getShiftEventsByID(shiftLogId);

      const sortedEvents = [...response.data].sort((a: ShiftEvent, b: ShiftEvent) => {
        const dateA = new Date(`${a.created_at}`);
        const dateB = new Date(`${b.created_at}`);
        return dateA.getTime() - dateB.getTime();
      });

      setEvents(sortedEvents);
    } catch (error: any) {
      console.error('Failed to load events:', error);
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to load events';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [shiftLogId]);

  const handleAddClick = () => {
    setSelectedEvent(null);
    setEventModalOpen(true);
  };

  const handleEditClick = (event: ShiftEvent) => {
    if (isSignedOff) return;
    setSelectedEvent(event);
    setEventModalOpen(true);
  };

  const handleModalClose = () => {
    setEventModalOpen(false);
    setSelectedEvent(null);
  };

  const handleModalSuccess = () => {
    setWasEditAction(!!selectedEvent);
    setEventModalOpen(false);
    setSelectedEvent(null);
    setShowSuccess(true);
    fetchEvents(); //  refresh timeline after adding/editing
  };

  return (
    <div className="mt-8">
      <div className="border-t border-gray-200 mb-6" />

      <h2 className="text-lg font-semibold text-blue-600 mb-6">Time & Event/Incident Description</h2>

      {loading ? (
        <p className="text-gray-500 text-sm">Loading events...</p>
      ) : (
        <div className="relative">
          {events.length > 0 && <div className="absolute left-[8.5px] top-[14px] bottom-0 w-0.5 bg-gray-300" />}

          {events.length > 0 ? (
            events.map((event) => (
              <TimelineItem
                key={event.id}
                time={event.event_time}
                description={event.description}
                onEditClick={() => handleEditClick(event)}
                isSignedOff={isSignedOff || readOnly}
              />
            ))
          ) : (
            <p className="text-gray-500 text-sm mb-8">No events found.</p>
          )}

          {!isSignedOff && !readOnly && (
            <div className="relative pl-8 pt-2">
              <button
                type="button"
                onClick={handleAddClick}
                className="absolute left-[4px] top-2 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700 z-10"
              >
                <span className="text-lg leading-none pb-0.5">+</span>
              </button>
              <span className="text-sm text-gray-500 cursor-pointer" onClick={handleAddClick}>
                Add Events
              </span>
            </div>
          )}
        </div>
      )}

      {/* EVENT MODAL (handles both add and edit) */}
      {eventModalOpen && (
        <EventModal
          selectedShiftId={shiftLogId}
          initialValues={
            selectedEvent
              ? {
                  id: selectedEvent.id,
                  time: selectedEvent.event_time,
                  description: selectedEvent.description,
                }
              : undefined
          }
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* SUCCESS MODAL */}
      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title={wasEditAction ? 'Event Updated' : 'Event Added'}
        message={wasEditAction ? 'The event has been successfully updated.' : 'The event has been successfully logged.'}
        buttonText="OK"
        onButtonClick={() => setShowSuccess(false)}
      />

      {/* Error Notification */}
      {showError && (
        <div className="fixed bottom-6 right-6 z-[100]">
          <div className="bg-red-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px] max-w-[500px] relative animate-slide-in">
            <button onClick={() => setShowError(false)} className="absolute top-3 right-3 text-white hover:text-gray-200 transition-colors">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Error</h4>
                <p className="text-sm">{apiError}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
